import { useEffect, useState, useRef } from "react";
import api, { mediaUrl, formatError } from "../lib/api";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Switch } from "../components/ui/switch";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "../components/ui/dialog";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";
import {
  Megaphone, Plus, Loader2, Tv, Check, Clock, Trash2, Upload, Timer,
} from "lucide-react";

const PRESETS = [
  { label: "1 day", days: 1 },
  { label: "3 days", days: 3 },
  { label: "1 week", days: 7 },
  { label: "2 weeks", days: 14 },
];

function countdown(expiresAt) {
  if (!expiresAt) return "No timer — until stopped";
  const ms = new Date(expiresAt).getTime() - Date.now();
  if (ms <= 0) return "expired";
  const d = Math.floor(ms / 86400000);
  const h = Math.floor((ms % 86400000) / 3600000);
  const m = Math.floor((ms % 3600000) / 60000);
  const s = Math.floor((ms % 60000) / 1000);
  if (d > 0) return `${d}d ${h}h ${m}m left`;
  if (h > 0) return `${h}h ${m}m ${s}s left`;
  return `${m}m ${s}s left`;
}

function NewAdDialog({ media, tvs, onClose, onCreated }) {
  const [selMedia, setSelMedia] = useState(null);
  const [every, setEvery] = useState(3);
  const [expiryMode, setExpiryMode] = useState("preset");
  const [presetDays, setPresetDays] = useState(7);
  const [customDt, setCustomDt] = useState("");
  const [allTvs, setAllTvs] = useState(true);
  const [selected, setSelected] = useState({});
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [localMedia, setLocalMedia] = useState(media);
  const fileRef = useRef();

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post("/media/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setLocalMedia((m) => [data, ...m]);
      setSelMedia(data.id);
      toast.success("Uploaded — now set the timer");
    } catch (err) {
      toast.error(formatError(err.response?.data?.detail) || "Upload failed");
    }
    setUploading(false);
  };

  const computeExpiry = () => {
    if (expiryMode === "preset") return new Date(Date.now() + presetDays * 86400000).toISOString();
    if (!customDt) return null;
    return new Date(customDt).toISOString();
  };
  const endLabel = () => {
    const iso = computeExpiry();
    if (!iso) return "—";
    return new Date(iso).toLocaleString([], { weekday: "short", month: "short", day: "numeric", hour: "numeric", minute: "2-digit" });
  };

  const create = async () => {
    if (!selMedia) return toast.error("Pick an ad image or video first");
    const expires_at = computeExpiry();
    if (!expires_at) return toast.error("Set an expiry date & time");
    if (new Date(expires_at).getTime() <= Date.now()) return toast.error("Expiry must be in the future");
    const tv_ids = allTvs ? null : Object.keys(selected).filter((k) => selected[k]);
    if (!allTvs && (!tv_ids || tv_ids.length === 0)) return toast.error("Pick at least one TV");
    setSaving(true);
    try {
      await api.post("/ads", { media_id: selMedia, tv_ids, every: Number(every), expires_at });
      toast.success("📢 Ad is live — it'll remove itself when the timer ends");
      onCreated();
      onClose();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not start ad");
    }
    setSaving(false);
  };

  return (
    <DialogContent className="max-w-2xl w-[calc(100%-1.5rem)] max-h-[90vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="text-lg md:text-xl pr-6 flex items-center gap-2"><Megaphone className="w-5 h-5 text-primary" /> New timed ad</DialogTitle>
        <DialogDescription className="sr-only">Pick an ad, set a timer and choose which TVs show it.</DialogDescription>
      </DialogHeader>

      <div className="space-y-5">
        {/* 1. Pick media */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <Label>1. Choose the ad</Label>
            <Button type="button" size="sm" variant="secondary" className="h-8 text-xs" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid="ad-upload-btn">
              {uploading ? <><Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> Uploading…</> : <><Upload className="w-3.5 h-3.5 mr-1" /> Upload new</>}
            </Button>
            <input ref={fileRef} type="file" accept="image/*,video/*" hidden onChange={onUpload} />
          </div>
          <div className="grid grid-cols-3 sm:grid-cols-4 gap-2 max-h-52 overflow-y-auto pr-1">
            {localMedia.map((m) => (
              <button type="button" key={m.id} onClick={() => setSelMedia(m.id)} data-testid={`ad-media-${m.id}`}
                className={`relative aspect-video bg-black rounded overflow-hidden border transition-colors ${selMedia === m.id ? "border-primary ring-2 ring-primary" : "border-border hover:border-muted-foreground"}`}>
                {m.type === "image"
                  ? <img src={mediaUrl(m.id)} alt="" className="w-full h-full object-cover" />
                  : <video src={mediaUrl(m.id)} className="w-full h-full object-cover" muted preload="metadata" playsInline />}
                {selMedia === m.id && <span className="absolute bottom-1 right-1 w-5 h-5 rounded-full bg-primary flex items-center justify-center"><Check className="w-3 h-3 text-white" /></span>}
              </button>
            ))}
            {localMedia.length === 0 && <p className="col-span-full text-sm text-muted-foreground py-4 text-center">No media yet — upload one above.</p>}
          </div>
        </div>

        {/* 2. Timer */}
        <div>
          <Label className="flex items-center gap-1.5"><Timer className="w-4 h-4" /> 2. Auto-expire timer</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            {PRESETS.map((p) => (
              <button type="button" key={p.days} data-testid={`ad-preset-${p.days}`}
                onClick={() => { setExpiryMode("preset"); setPresetDays(p.days); }}
                className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${expiryMode === "preset" && presetDays === p.days ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
                {p.label}
              </button>
            ))}
            <button type="button" data-testid="ad-preset-custom"
              onClick={() => setExpiryMode("custom")}
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${expiryMode === "custom" ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              Custom date & time
            </button>
          </div>
          {expiryMode === "custom" && (
            <Input type="datetime-local" value={customDt} min={new Date().toISOString().slice(0, 16)}
              onChange={(e) => setCustomDt(e.target.value)} className="mt-2" data-testid="ad-custom-datetime" />
          )}
          <p className="text-sm mt-2 flex items-center gap-1.5" data-testid="ad-ends-label">
            <Clock className="w-4 h-4 text-primary" /> Ad ends: <b className="text-primary">{endLabel()}</b>
          </p>
        </div>

        {/* 3. How often + TVs */}
        <div className="flex items-center justify-between gap-3">
          <Label>Show it every</Label>
          <Select value={String(every)} onValueChange={(v) => setEvery(Number(v))}>
            <SelectTrigger className="w-40" data-testid="ad-every-select"><SelectValue /></SelectTrigger>
            <SelectContent>
              {[2, 3, 4, 5, 6, 8].map((n) => <SelectItem key={n} value={String(n)}>every {n} slides</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <p className="text-[11px] text-amber-400/90 -mt-3">The ad rotates in with your slides — needs a playlist/offers running on the TV.</p>

        <div>
          <Label>3. Which TVs show this ad?</Label>
          <div className="flex flex-wrap gap-2 mt-2">
            <button type="button" onClick={() => { setAllTvs(true); setSelected({}); }} data-testid="ad-tv-all"
              className={`px-3 py-1.5 rounded-full text-sm border transition-colors ${allTvs ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
              <Check className={`w-3.5 h-3.5 inline mr-1 ${allTvs ? "" : "opacity-0"}`} />All TVs
            </button>
            {tvs.map((tv) => {
              const on = !allTvs && selected[tv.id];
              return (
                <button key={tv.id} type="button" data-testid={`ad-tv-${tv.id}`}
                  onClick={() => setSelected((s) => { const ns = { ...s, [tv.id]: !s[tv.id] }; setAllTvs(false); return ns; })}
                  className={`px-3 py-1.5 rounded-full text-sm border transition-colors flex items-center gap-1.5 ${on ? "border-primary bg-primary/10 text-primary" : "border-border bg-secondary/40 hover:border-muted-foreground"}`}>
                  <Tv className="w-3.5 h-3.5" /> {tv.name}
                  {on && <Check className="w-3.5 h-3.5" />}
                </button>
              );
            })}
            {tvs.length === 0 && <p className="text-sm text-muted-foreground py-1">No TVs paired yet.</p>}
          </div>
          <p className="text-[11px] text-muted-foreground mt-1.5">
            {allTvs ? "Showing on every paired screen." : `Showing on ${Object.values(selected).filter(Boolean).length} selected screen(s).`}
          </p>
        </div>
      </div>

      <DialogFooter className="mt-2">
        <Button variant="secondary" onClick={onClose}>Cancel</Button>
        <Button onClick={create} disabled={saving} data-testid="ad-create-btn">
          {saving ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Starting…</> : <><Plus className="w-4 h-4 mr-2" /> Start ad</>}
        </Button>
      </DialogFooter>
    </DialogContent>
  );
}

export default function Ads() {
  const [ads, setAds] = useState([]);
  const [media, setMedia] = useState([]);
  const [tvs, setTvs] = useState([]);
  const [open, setOpen] = useState(false);
  const [, setTick] = useState(0);

  const loadAds = () => api.get("/ads").then((r) => setAds(r.data)).catch(() => {});
  useEffect(() => {
    loadAds();
    api.get("/media").then((r) => setMedia(r.data)).catch(() => {});
    api.get("/tvs").then((r) => setTvs(r.data)).catch(() => {});
  }, []);

  // live countdown + auto-drop expired ads from the list
  useEffect(() => {
    const t = setInterval(() => {
      setTick((x) => x + 1);
      setAds((prev) => prev.filter((a) => !a.expires_at || new Date(a.expires_at).getTime() > Date.now()));
    }, 1000);
    return () => clearInterval(t);
  }, []);

  const stopAd = async (id) => {
    try {
      await api.delete(`/ads/${id}`);
      toast.success("Ad stopped");
      loadAds();
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail) || "Could not stop");
    }
  };

  const mediaOf = (id) => media.find((m) => m.id === id);

  return (
    <div className="p-4 md:p-8 pb-24 md:pb-8 max-w-4xl">
      <div className="mb-5 flex items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="text-2xl md:text-3xl font-display font-bold tracking-tight flex items-center gap-2">
            <Megaphone className="w-6 h-6 text-primary" /> Timed Ads
          </h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Run an ad on the TVs you choose. It removes itself when the timer ends — no stale ads left on screen.
          </p>
        </div>
        <Button className="flex-shrink-0" onClick={() => setOpen(true)} data-testid="new-ad-btn">
          <Plus className="w-4 h-4 mr-1.5" /> New Ad
        </Button>
      </div>

      {ads.length === 0 ? (
        <div className="rounded-xl border border-dashed border-border p-10 text-center" data-testid="ads-empty">
          <Megaphone className="w-10 h-10 mx-auto mb-3 text-muted-foreground/40" />
          <p className="text-muted-foreground">No active ads. Click <b>New Ad</b> to run one with an auto-expiry timer.</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3" data-testid="ads-list">
          {ads.map((a) => {
            const m = mediaOf(a.media_id);
            return (
              <div key={a.id} className="rounded-xl border border-border bg-card overflow-hidden" data-testid={`ad-card-${a.id}`}>
                <div className="aspect-video bg-black">
                  {m && (m.type === "image"
                    ? <img src={mediaUrl(a.media_id)} alt="" className="w-full h-full object-cover" />
                    : <video src={mediaUrl(a.media_id)} className="w-full h-full object-cover" muted preload="metadata" playsInline />)}
                </div>
                <div className="p-3">
                  <div className="flex items-center justify-between gap-2">
                    <p className="font-semibold text-sm truncate">{a.name}</p>
                    <Button size="sm" variant="ghost" className="h-7 text-xs px-2 flex-shrink-0 text-muted-foreground hover:text-destructive"
                      onClick={() => stopAd(a.id)} data-testid={`ad-stop-${a.id}`}>
                      <Trash2 className="w-3.5 h-3.5 mr-1" /> Stop
                    </Button>
                  </div>
                  <div className="flex items-center gap-1.5 text-primary text-sm font-medium mt-1" data-testid={`ad-countdown-${a.id}`}>
                    <Clock className="w-3.5 h-3.5" /> {countdown(a.expires_at)}
                  </div>
                  <p className="text-[11px] text-muted-foreground mt-1">
                    <Tv className="w-3 h-3 inline mr-1" />
                    {a.tv_ids ? `${a.tv_names?.length || a.tv_ids.length} TV(s): ${(a.tv_names || []).join(", ")}` : "All TVs"}
                    {" · "}every {a.every} slides
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        {open && <NewAdDialog media={media} tvs={tvs} onClose={() => setOpen(false)} onCreated={loadAds} />}
      </Dialog>
    </div>
  );
}
